import { useClient } from '@cortex/react-query';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useOptionalAuth } from '../auth/AuthProvider';
import { listMyOrgMembers } from '../lib/orgs';

/** What a reader sees for a user the console cannot name. Never a raw id. */
export const unknownUserLabel = 'a teammate';
/** Who did it when the platform did: template upgrades, provisioning. */
export const formantActorLabel = 'Formant';

interface IFormantUser {
  email?: unknown;
  firstName?: unknown;
  lastName?: unknown;
}

function formantUserName(u: IFormantUser): string | null {
  const first = typeof u.firstName === 'string' ? u.firstName : '';
  const last = typeof u.lastName === 'string' ? u.lastName : '';
  const name = [first, last].filter(Boolean).join(' ');
  if (name) return name;
  return typeof u.email === 'string' && u.email ? u.email : null;
}

/**
 * Org members by subject, from the cortex org endpoint. Under a Formant org
 * (Coherence sign-in) that endpoint has no Keycloak realm and answers 404:
 * the map is then empty and `useUserLabel` goes to the Formant users API.
 */
function useMemberLabels(): ReadonlyMap<string, string> {
  const client = useClient();
  const members = useQuery({
    queryKey: ['org-members'],
    queryFn: () => listMyOrgMembers(client),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return useMemo(() => {
    const map = new Map<string, string>();
    for (const member of members.data ?? []) map.set(member.id, member.name ?? member.email);
    return map;
  }, [members.data]);
}

/**
 * Display name for one user id (a Keycloak subject or a Formant user id):
 * the org member list first, then `GET /v1/admin/users/:id` on the Formant
 * API with the session's token (a Coherence session's token IS a Formant
 * token; under Keycloak the call fails and the fallback applies). Null ids
 * and system actors are not looked up. Never returns the id itself.
 */
export function useUserLabel(id: string | null | undefined): string {
  const members = useMemberLabels();
  const auth = useOptionalAuth();
  const fromMembers = id ? members.get(id) : undefined;
  const apiBase = import.meta.env.VITE_FORMANT_API_URL ?? 'https://api-dev.formant.io';
  const token = auth?.token ?? null;
  const formantUser = useQuery({
    queryKey: ['formant-user', id],
    enabled: !!id && !fromMembers && !!token,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
    queryFn: async () => {
      const res = await fetch(`${apiBase}/v1/admin/users/${encodeURIComponent(id ?? '')}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      return formantUserName((await res.json()) as IFormantUser);
    },
  });
  if (!id) return unknownUserLabel;
  return fromMembers ?? formantUser.data ?? unknownUserLabel;
}
